import { useCallback, useState } from 'react';
import {
    PointerSensor,
    pointerWithin,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import type { LeadCloudFileType } from '@econtractor/types';
import { canDropOnParent, itemFromDraggable, parentPathFromDroppable } from './fileDropRules';

interface UseFilesContainerDndParams {
    enabled: boolean;
    onMove: (item: LeadCloudFileType, parentPath: string) => Promise<boolean>;
}

const useFilesContainerDnd = ({ enabled, onMove }: UseFilesContainerDndParams) => {
    const [activeItem, setActiveItem] = useState<LeadCloudFileType | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        if (!enabled) return;
        setActiveItem(itemFromDraggable(event.active.data.current));
    }, [enabled]);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const item = itemFromDraggable(event.active.data.current);
            const parentPath = parentPathFromDroppable(event.over);
            setActiveItem(null);
            if (!enabled || !item || parentPath == null) return;
            if (!canDropOnParent(item, parentPath)) return;

            const ignoreNextClick = (clickEvent: MouseEvent) => {
                clickEvent.preventDefault();
                clickEvent.stopPropagation();
            };
            window.addEventListener('click', ignoreNextClick, true);
            window.setTimeout(() => window.removeEventListener('click', ignoreNextClick, true), 300);

            void onMove(item, parentPath);
        },
        [enabled, onMove],
    );

    const handleDragCancel = useCallback(() => {
        setActiveItem(null);
    }, []);

    return {
        sensors,
        collisionDetection: pointerWithin,
        activeItem,
        handleDragStart,
        handleDragEnd,
        handleDragCancel,
    };
};

export default useFilesContainerDnd;
